import type { CSSProperties, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@artosera/ui';
import { formaterDuree } from '../../../../lib/format.js';
import type { TravailLot } from '../../../../lib/reprise.js';
import styles from './Plan.module.css';

function nombre(valeur: number): string {
  return valeur.toLocaleString('fr-FR');
}

/**
 * Où en est le travail d'un lot : la barre `faits/total`, l'étape (essai,
 * import, annulation), le temps restant estimé par l'API et la priorité. Les
 * chiffres sont ceux de l'API (`avancement`), jamais recalculés ici ; la barre
 * ne fait que les dessiner. `compact` : la ligne de la liste des lots.
 */
export function ProgressionTravail({
  travail,
  actif,
  compact = false,
}: {
  travail: TravailLot | null | undefined;
  /** Le travail tourne : on le dit, même sans avancement encore écrit. */
  actif: boolean;
  compact?: boolean;
}): JSX.Element | null {
  const { t } = useTranslation('console');
  const avancement = travail?.avancement ?? null;
  if (!travail || (!actif && avancement === null)) return null;

  const total = avancement?.total ?? null;
  const pourcentage =
    avancement !== null && total !== null && total > 0
      ? Math.min(100, Math.round((avancement.faits / total) * 100))
      : null;
  const etape = avancement
    ? String(t(`plan.progression.etapes.${avancement.etape}`, { defaultValue: avancement.etape }))
    : null;
  // La reprise passe en priorité basse dans la file (`api.md` § 12.18) ; la fiche d'un lot ne la porte pas encore.
  const priorite = travail.priorite ?? 'basse';
  const restant = avancement?.restantEstimeMs ?? null;

  return (
    <div className={`${styles.progression} ${compact ? styles.progressionCompacte : ''}`}>
      {avancement ? (
        <>
          <div
            className={styles.piste}
            role="progressbar"
            aria-label={t('plan.progression.libelle')}
            aria-valuemin={0}
            aria-valuemax={total ?? undefined}
            aria-valuenow={total === null ? undefined : avancement.faits}
            aria-valuetext={
              total === null
                ? t('plan.progression.faitsSansTotal', { faits: nombre(avancement.faits) })
                : t('plan.progression.faits', {
                    faits: nombre(avancement.faits),
                    total: nombre(total),
                  })
            }
            data-indeterminee={pourcentage === null}
          >
            <span
              className={styles.remplissage}
              style={{ '--avance': `${String(pourcentage ?? 0)}%` } as CSSProperties}
            />
          </div>
          <div className={styles.progressionMeta}>
            <span className={styles.progressionChiffres}>
              {total === null
                ? t('plan.progression.faitsSansTotal', { faits: nombre(avancement.faits) })
                : t('plan.progression.faits', {
                    faits: nombre(avancement.faits),
                    total: nombre(total),
                  })}
            </span>
            {etape ? <span>{t('plan.progression.etape', { etape })}</span> : null}
            {compact ? null : (
              <span>
                {restant === null
                  ? t('plan.progression.restantInconnu')
                  : t('plan.progression.restant', {
                      duree:
                        restant < 60_000
                          ? t('plan.progression.moinsDUneMinute')
                          : formaterDuree(restant),
                    })}
              </span>
            )}
            {compact ? null : (
              <Badge tone="neutral" title={t('plan.progression.prioriteAide')}>
                {t('plan.progression.priorite', {
                  niveau: t(`plan.progression.priorites.${priorite}`),
                })}
              </Badge>
            )}
          </div>
        </>
      ) : (
        <p className={styles.progressionMeta} aria-live="polite">
          {t('plan.progression.demarrage')}
        </p>
      )}
    </div>
  );
}
